import React, { useEffect, useRef, useState } from "react";
import { Send, Paperclip, Mic, Sparkles, Link2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageContainer } from "@/components/Page";

const OptimusAssistant = () => {
  const { user, onboarding } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef();

  useEffect(() => {
    api.get("/optimus/history").then(({ data }) => setMessages(data.messages || [])).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    if (!text.trim() || sending) return;
    setSending(true);
    const myMsg = { role: "user", content: text };
    setMessages((m) => [...m, myMsg]);
    setInput("");
    try {
      const { data } = await api.post("/optimus/chat", { message: text, history: messages });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't process that — try again." }]);
    } finally { setSending(false); }
  };

  const quickPills = [
    "Create a social post for this weekend",
    "Promote an event",
    "Get content ideas for next week",
  ];

  return (
    <PageContainer>
      <div className="text-center mb-10" data-testid="optimus-welcome">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#EDE9FE] flex items-center justify-center mb-4">
          <Sparkles className="text-[#7C3AED]" size={26} />
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Welcome back, {user?.name?.split(' ')[0]}.</h1>
        <p className="text-gray-500 mt-2">What's next for {onboarding?.business_name || "your business"}?</p>
      </div>

      {messages.length === 0 ? (
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="optimus-quick-pills">
          {quickPills.map((p) => (
            <button key={p} onClick={() => send(p)} className="op-pill-inactive" data-testid={`optimus-pill-${p.substring(0,10).replace(/\s/g,'-')}`}>{p}</button>
          ))}
        </div>
      ) : (
        <div className="max-w-3xl mx-auto space-y-4 mb-8" data-testid="optimus-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === 'user' ? 'bg-[#7C3AED] text-white' : 'op-card'}`}>
                <p className="text-sm whitespace-pre-wrap">{m.content}</p>
              </div>
            </div>
          ))}
          {sending && <div className="flex justify-start"><div className="op-card px-4 py-3"><div className="flex gap-1"><span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" /><span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" /><span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" /></div></div></div>}
          <div ref={endRef} />
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="max-w-3xl mx-auto" data-testid="optimus-form">
        <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-full pl-5 pr-2 py-2 focus-within:border-[#7C3AED] focus-within:ring-1 focus-within:ring-[#7C3AED]">
          <Paperclip className="text-gray-400" size={18} />
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Optimus Prime..." className="flex-1 outline-none text-sm" data-testid="optimus-input" />
          <button type="button" className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" data-testid="optimus-mic-btn">
            <Mic size={18} />
          </button>
          <button type="submit" disabled={sending} className="w-9 h-9 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white flex items-center justify-center hover:opacity-90 disabled:opacity-50" data-testid="optimus-send-btn">
            <Send size={16} />
          </button>
        </div>
      </form>

      <div className="max-w-3xl mx-auto mt-10 op-card p-5 flex items-center gap-4" data-testid="optimus-social-card">
        <div className="w-10 h-10 rounded-full bg-[#EDE9FE] flex items-center justify-center"><Link2 className="text-[#7C3AED]" size={18} /></div>
        <div className="flex-1">
          <p className="font-semibold text-gray-900">Connect your social media accounts</p>
          <p className="text-sm text-gray-500">So Optimus Prime can post and analyze for you automatically.</p>
        </div>
        <button className="op-btn-secondary text-sm py-2">Connect</button>
      </div>
    </PageContainer>
  );
};
export default OptimusAssistant;
